'use client'

import { ITravelPointQuote } from '@repo/sdk'
import React, { createContext, PropsWithChildren, useContext, useMemo, useState } from 'react'
import { Control, useController, UseControllerReturn } from 'react-hook-form'

interface Context {
  control: Control<{ drops: ITravelPointQuote[] }>
  dateControl: UseControllerReturn<
    {
      drops: ITravelPointQuote[]
    },
    `drops.${number}.date`
  >
  dateState: [Date | undefined, React.Dispatch<React.SetStateAction<Date | undefined>>]
  index: number
  isAdmin?: boolean
  openState: [boolean, React.Dispatch<React.SetStateAction<boolean>>]
  quickSelectState: [QuickSelectTypes, React.Dispatch<React.SetStateAction<QuickSelectTypes>>]
  quickSelectUsed: boolean
  timeControl: UseControllerReturn<
    {
      drops: ITravelPointQuote[]
    },
    `drops.${number}.time`
  >
  timeState: [
    ITravelPointQuote['time'],
    React.Dispatch<React.SetStateAction<ITravelPointQuote['time']>>,
  ]
}

type QuickSelectTypes = 'next-month' | 'next-week' | 'not-sure' | undefined

const DateTimeInputContext = createContext<Context>({} as Context)

interface Props {
  control: Control<{ drops: ITravelPointQuote[] }>
  index: number
  isAdmin?: boolean
}

export const DateTimeInputProvider = ({
  children,
  control,
  index,
  isAdmin = false,
}: PropsWithChildren<Props>) => {
  const dateControl = useController({
    control,
    name: `drops.${index}.date`,
  })
  const timeControl = useController({
    control,
    name: `drops.${index}.time`,
  })

  const openState = useState(false)
  const dateState = useState<Date | undefined>()
  const timeState = useState<ITravelPointQuote['time']>(
    timeControl.field.value || {
      type: 'between',
    },
  )
  const quickSelectState = useState<QuickSelectTypes>()
  const [quickSelect] = quickSelectState

  const quickSelectUsed = useMemo(() => {
    return !!quickSelect
  }, [quickSelect])

  return (
    <DateTimeInputContext.Provider
      value={{
        control,
        dateControl,
        dateState,
        index,
        isAdmin,
        openState,
        quickSelectState,
        quickSelectUsed,
        timeControl,
        timeState,
      }}
    >
      {children}
    </DateTimeInputContext.Provider>
  )
}

export const useDateTimeInput = () => {
  const context = useContext(DateTimeInputContext)

  if (!context) {
    throw new Error('useDateTimeInput must be used within a DateTimeInputProvider')
  }

  return context
}
